import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faStar as faStarRegular, faFutbol } from "@fortawesome/pro-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons";
import ScaleFade from "../../Animated/ScaleFade";
import { ClavaContext } from "../../../../config/contexts";
import { MatchSmallElement, PlayerMatchData } from "../../../../config/types";
import MatchStatusDisplay from "../MatchStatusDisplay";
import TeamName from "../../Team/TeamName";
import { formatWeekdayDayMonth, matchStatusDate } from "../../../../config/utils";
import { SportEnum } from "../../../../client/api";
import { showTranslated, translate } from "../../../../config/translator";
import { selectLeagues } from "../../../../selectors";
import RoundedRectangle from "../../RoundedRectangle";
import { pathLeagueView, pathMatch } from "../../../../config/routes";
import { addFavorite, removeFavorite } from "../../../../store/actions/userActions";
import { RootState } from "../../../../store";
import IconWithBackground from "../../IconWithBackground";
import PlayerRating from "../../PlayerRating";
import MinutesDisplay from "../../MinutesDisplay";

export type { PlayerMatchData };

type MatchTileProps = {
  match: MatchSmallElement;
  banner?: "none" | "date-league" | "league-only";
  isMainLeagueOverride?: boolean;
  playerData?: PlayerMatchData;
};

const MatchTile: React.FC<MatchTileProps> = ({
                                               match,
                                               banner: bannerProp,
                                               isMainLeagueOverride,
                                               playerData
                                             }) => {
  const banner = bannerProp ?? "none";
  const { l } = useContext(ClavaContext);
  const dispatch = useDispatch();
  const leagues = useSelector(selectLeagues);
  const favorites = useSelector((state: RootState) => state.user.favorites);
  const user = useSelector((state: RootState) => state.user.value);
  const navigate = useNavigate();
  const startMs = useMemo(
    () => new Date(match.startTime).getTime(),
    [match.startTime]
  );
  const endMs = useMemo(
    () => (match.endTime ? new Date(match.endTime).getTime() : NaN),
    [match.endTime]
  );
  const [status, setStatus] = useState(
    matchStatusDate(startMs, match.fullMatchLength, endMs)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(matchStatusDate(startMs, match.fullMatchLength, endMs));
    }, 5000);
    return () => clearInterval(interval);
  }, [endMs, match.fullMatchLength, startMs]);

  const isCancelled = match.cancelled;
  const live = !isCancelled && typeof status === "number";
  const isMainLeague = useMemo(() => {
    if (typeof isMainLeagueOverride === "boolean") return isMainLeagueOverride;

    const leagueId = match.leagueCupId;
    if (leagueId == null) return false;

    const league = leagues.find(lea => lea.id === leagueId);
    return !!league?.main;
  }, [leagues, match.leagueCupId, isMainLeagueOverride]);

  const isFavorited = useMemo(
    () => favorites.some(fav => fav.type === "match" && fav.id === match.id),
    [favorites, match.id]
  );

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFavorited) {
      removeFavorite(dispatch, { id: match.id, type: "match" });
    } else {
      addFavorite(dispatch, { id: match.id, type: "match" });
    }
  };

  const renderBanner = () => {
    const d = new Date(startMs);
    const bannerDate = formatWeekdayDayMonth(d, l);
    const leagueName = match.leagueCupName ? showTranslated(match.leagueCupName, l) : "";
    const leagueId = match.leagueCupId;

    const showDate = banner === "date-league";
    const showLeague = banner === "date-league" || banner === "league-only";
    const forceShowLeague = banner === "league-only";
    const enablePointerEvents = forceShowLeague || !isMainLeague;

    return (
      <RoundedRectangle
        className={`flex text-font-600 dark:text-font-600-dark text-xs font-normal whitespace-nowrap items-center justify-between pt-2 pb-6 -mb-5 px-3 z-10 ${enablePointerEvents ? "" : "pointer-events-none"} ${isPast
          ? "bg-bg-100 dark:bg-bg-100-dark"
          : isFuture
            ? "bg-primary-3 dark:bg-primary-3-dark"
            : (isMainLeague
              ? "bg-primary-50 dark:bg-primary-50-dark"
              : "bg-bg-100 dark:bg-bg-100-dark")
        }`}
      >
        {showDate && (
          <span>
            {bannerDate}
          </span>
        )}
        {showLeague && (forceShowLeague || !isMainLeague) && leagueId && (
          <button
            type="button"
            onClick={() => navigate(pathLeagueView(leagueId, "table"))}
            className="flex flex-row items-center justify-start gap-1.5 text-font-600 dark:text-font-600-dark text-xs font-normal hover:text-primary dark:hover:text-primary transition-colors"
          >
            <span className="overflow-hidden text-ellipsis max-w-48">{leagueName}</span>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 mb-0.5 shrink-0" />
          </button>
        )}
      </RoundedRectangle>
    );
  };

  const now = Date.now();
  const isPast = Number.isFinite(endMs) && endMs <= now;
  const isFuture = startMs > now;

  const hasBanner = banner !== "none";
  const canceledOpacityClass = isCancelled ? "opacity-60" : "";
  const team1Goal = isCancelled ? undefined : status === "future" ? undefined : match.goal1;
  const team2Goal = isCancelled ? undefined : status === "future" ? undefined : match.goal2;


  return (
    <div className={`mb-2 w-full ${canceledOpacityClass}`}>

      {hasBanner && renderBanner()}
      <ScaleFade isOpen hover className="block relative z-10">
        <NavLink
          to={pathMatch(match.id)}
          className="block"
          aria-label={translate("match", l)}
        >
          <RoundedRectangle
            className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark relative"
            hasBorder
            hasTitle={false}
          >
            <div className="flex flex-row items-stretch justify-start">
              <div
                className="min-w-14 max-w-14 w-14 shrink-0 flex flex-col items-center justify-center border-light-gray dark:border-light-gray-dark border-r">
                {isCancelled ? (
                <span className="text-red text-xs font-semibold uppercase tracking-wide">
                  {translate("cancelledShort", l)}
                </span>
                ) : (
                  <MatchStatusDisplay
                    startDate={startMs}
                    endTime={endMs}
                    sectionAmount={match.sectionAmount}
                    sectionDuration={match.sectionDuration}
                    pauseDuration={match.pauseDuration}
                    hideLive={match.sports === SportEnum.SOCCER}
                    hideMinute={match.sports === SportEnum.HOCKEY}
                    compact
                  />
                )}
              </div>
              <div
                className="flex-1 min-w-0 px-2 py-1 flex flex-col text-font dark:text-font-dark text-xs justify-center">
                <TeamName
                  team={match.team1}
                  goal={playerData ? undefined : team1Goal}
                  live={live}
                  size="lg"
                />
                <TeamName
                  team={match.team2}
                  goal={playerData ? undefined : team2Goal}
                  live={live}
                  size="lg"
                />
              </div>
              {playerData && (
                <div className="flex items-center justify-center gap-3 px-3 py-2">
                  {playerData.amountMinutesPlayed > 0 && (
                    <MinutesDisplay minutes={playerData.amountMinutesPlayed} size="lg" />
                  )}
                  <PlayerRating rating={playerData.rating} size="lg" />
                  <div className="flex items-center gap-0">
                    {playerData.amountRedCards > 0 && (
                      <>
                        {Array.from({ length: playerData.amountRedCards }, (_, i) => {
                          const uniqueId = `${match.id}-red-${Date.now()}-${Math.random()}-${i}`;
                          return (
                            <div
                              key={uniqueId}
                              className="bg-white dark:bg-bg-100-dark border border-light-gray dark:border-light-gray-dark rounded-full w-8 h-8 flex items-center justify-center"
                              style={{ marginLeft: i > 0 ? "-4px" : "0" }}
                            >
                              <div className="w-2.5 h-4 rounded-sm bg-red-card" />
                            </div>
                          );
                        })}
                      </>
                    )}
                    {playerData.amountYellowCards > 0 && (
                      <>
                        {Array.from({ length: playerData.amountYellowCards }, (_, i) => {
                          const uniqueId = `${match.id}-yellow-${Date.now()}-${Math.random()}-${i}`;
                          return (
                            <div
                              key={uniqueId}
                              className="bg-white dark:bg-bg-100-dark border border-light-gray dark:border-light-gray-dark rounded-full w-8 h-8 flex items-center justify-center"
                              style={{ marginLeft: (playerData.amountRedCards > 0 || i > 0) ? "-4px" : "0" }}
                            >
                              <div className="w-2.5 h-4 rounded-sm bg-yellow-card" />
                            </div>
                          );
                        })}
                      </>
                    )}
                    {playerData.amountGoals > 0 && (
                      <>
                        {Array.from({ length: playerData.amountGoals }, (_, i) => {
                          const uniqueId = `${match.id}-goal-${Date.now()}-${Math.random()}-${i}`;
                          return (
                            <div
                              key={uniqueId}
                              className="w-8 h-8 flex items-center justify-center"
                              style={{ marginLeft: (playerData.amountRedCards > 0 || playerData.amountYellowCards > 0 || i > 0) ? "-4px" : "0" }}
                            >
                              <IconWithBackground
                                icon={faFutbol}
                                className="w-4 h-4 text-font dark:text-font-dark"
                                backgroundClassName="bg-white dark:bg-bg-100-dark border border-light-gray dark:border-light-gray-dark w-8 h-8"
                                borderRadius="rounded-full"
                                bigPadding
                              />
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
              {playerData && (
                <div
                  className={`w-14 min-w-14 max-w-14 shrink-0 flex flex-col items-center justify-center border-l border-light-gray dark:border-light-gray-dark ${!live ? "rounded-r-xl" : ""}`}
                >
                  <span className={`text-base font-semibold ${live ? "text-red" : "text-font dark:text-font-dark"}`}>
                    {team1Goal ?? 0}
                  </span>
                  <span className={`text-base font-semibold ${live ? "text-red" : "text-font dark:text-font-dark"}`}>
                    {team2Goal ?? 0}
                  </span>
                </div>
              )}
              {user && !playerData && (
                  <div
                      className={`w-14 min-w-14 max-w-14 shrink-0 flex items-center justify-center border-l border-light-gray dark:border-light-gray-dark ${!live ? "rounded-r-xl" : ""} `}>
                      <button
                          type="button"
                          onClick={handleFavoriteClick}
                          className="transition-transform hover:scale-110"
                          aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}>
                          <FontAwesomeIcon
                              icon={isFavorited ? faStarSolid : faStarRegular}
                              className={`h-5 ${isFavorited ? "text-primary" : "text-font-600 dark:text-font-600-dark"}`}
                          />
                      </button>
                  </div>

              )}
            </div>
          </RoundedRectangle>
        </NavLink>
      </ScaleFade>
    </div>
  );
};

export default MatchTile;
